// import { ExpandArea } from '@intigral/ui';
import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { getBannerImages, getAdConfig } from '../utils/helper';
import type {
    AdConfig,
    AdBreaksEvent,
    AdBreakInstance,
    AdBreaksStartedEvent,
    OutstreamAdCarousel,
} from '../types/iCarousel.types';
import user from '../../../helpers/user';
import { isFreeTier } from '@intigral/utils/lib/user';
import h, { getCountry } from '../../../helpers/common';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import { staticDeviceInfo } from '../../../hooks/useCSAI';
import { BaseBitmovinPlayer } from '../../player/components/BaseBitimovinPlayer';
import { PlayerAPI, PlayerConfig, PlayerEvent } from 'bitmovin-player';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import useAvod from '../../../hooks/useAvod';
import {
    createAdsUI,
    hideNoAdCta,
    setNoAdCtaAdBreakStartTime,
    setOutstreamPlayerForNoAdCta,
    setupNoAdCtaFromConfig,
    setOutstreamActiveAdIdForNoAdCta,
} from '../../player/utils/AdsUiUtils';
import type { IProfileData, IUserFullObject } from '@intigral/types';
import {
    setYBPluginOptions,
    setYBAdPluginOptions,
    removeYBAdapter,
    removeYBAdsAdapter,
    updateYouboraAnalytics,
} from '../../../helpers/youbora';
import { playerTypes } from '../../../config/constants';
import styles from '../../player/styles/player.module.sass';
import { avodHandler } from '../../../helpers/avodHandler';
import { Button } from '@intigral/shared';
import { useResumeAdPlayback } from '../../player';
import { useInView } from 'react-intersection-observer';
import { isGuestUser } from '../../../config/guestMode';

const adBannerHandler = {
    adsStarted: false,
    userExitDuringAd: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    beforeSourceLoaded() {
        // Callback triggered before the ad source is loaded.
    },
};

const AdBanner: React.FC<{ carousel: OutstreamAdCarousel; name: string }> = ({ carousel, name }) => {
    // ==========================================
    // Refs & State
    // ==========================================
    /** From ExpandArea only — do not toggle off when an ad pod finishes, or visibility will never re-fire while user stays in zone */
    const [expandVisible, setExpandVisible] = useState<boolean>(false);
    /** After the current pod completes, show poster until user leaves the banner scroll zone and comes back */
    const [adPodCompleted, setAdPodCompleted] = useState<boolean>(false);
    const [playerMountKey, setPlayerMountKey] = useState<number>(0);

    const playerInstanceRef = useRef<PlayerAPI | null>(null);
    const currentAdBreakRef = useRef<AdBreaksEvent | null>(null);
    const currentAdInstanceRef = useRef<AdBreakInstance | null>(null);
    /** ExpandArea can fire onVisibilityChange(true) on every scroll tick while in range — only treat false→true as a new visit */
    const expandWasVisibleRef = useRef<boolean>(false);

    // ==========================================
    // Hooks & Contexts
    // ==========================================
    const { track } = useContext(MixpanelContext);
    const { updateAdsUI, onAdBeginUI } = useAvod('outstream', false, null as any);
    const { config } = useConfigurationSections();

    const { inView, ref } = useInView({ threshold: 0.9 });

    // ==========================================
    // Memos (Variables & Derived State)
    // ==========================================
    const userInfo: IUserFullObject = useMemo(() => user.getUserInfo(), []);
    const profileInfo: IProfileData = useMemo(() => user.getProfile(), []);
    const isFreeTierSub = useMemo(() => isFreeTier(userInfo), [userInfo]);
    const isGuest = isGuestUser(userInfo);
    const { background } = useMemo(() => getBannerImages(carousel), [carousel]);
    const adConfig: AdConfig = useMemo(() => getAdConfig(carousel), [carousel]);

    const playerKey = config.player?.bitmovin_app_key || '';
    /** Only gate on tier; outstream replays each time the user scrolls the banner into view */
    const shouldDisableBanner = !isFreeTierSub && !isGuest;

    useEffect(() => {
        if (shouldDisableBanner) return;
        try {
            avodHandler.initialize(track, {}, true);
        } catch {
            // ignore
        }
        return () => {
            try {
                avodHandler.destroy();
            } catch {
                // ignore
            }
        };
    }, [track, shouldDisableBanner]);

    // ==========================================
    // Memos (Configurations)
    // ==========================================
    const supplyParameters = useMemo(() => {
        if (shouldDisableBanner) return null;
        const { adTagParams } = config.avodConfigs;
        return {
            w: window.innerWidth.toString(),
            h: window.innerHeight.toString(),
            cb: Date.now().toString(),
            url: adTagParams.domain,
            app_bundle: adTagParams.appBundle,
            app_name: adTagParams.appName,
            country: getCountry() as string,
            did: user.getUserDeviceId() as string,
            ifa_type: adTagParams.ifaType,
            content_genre: '',
            language: h.getLang() as string,
            pod_max_dur: (adTagParams.pod_max_dur || 90).toString(),
            coppa: profileInfo?.kidsMode ? '1' : '0',
            rating: profileInfo?.newParentalControl || profileInfo?.parentalControl || '',
            ip: h.getUserIP() as string,
            ...staticDeviceInfo,
            dnt: (adTagParams.dnt || 0).toString(),
            lmt: (adTagParams.lmt || 0).toString(),
        };
    }, [shouldDisableBanner, config.avodConfigs]);

    const adTagUrl = useMemo(() => {
        if (shouldDisableBanner) return '';
        const routeID: number = adConfig?.outstreamSpringServeRouteID;
        if (!routeID) {
            return '';
        }
        try {
            const endpoint = new URL(adConfig.springServeBaseURL + routeID.toString());
            if (supplyParameters) {
                for (const [paramKey, paramValue] of Object.entries(supplyParameters)) {
                    endpoint.searchParams.set(paramKey, paramValue.toString());
                }
            }
            return endpoint.toString();
        } catch {
            return '';
        }
    }, [adConfig, supplyParameters, shouldDisableBanner]);

    const playerConfig: PlayerConfig | null = useMemo(() => {
        if (shouldDisableBanner) return null;
        return {
            key: playerKey,
            playback: { autoplay: true, muted: true },
            advertising: {
                adBreaks: [
                    {
                        // @ts-expect-error ts(2352)
                        id: 'adBanner',
                        position: 'pre',
                        tag: { url: adTagUrl, type: 'vast' },
                    },
                ],
            },
            ui: { metadata: { markers: [] }, autoUiVariantResolve: true },
            tweaks: { fairplay_ignore_duplicate_init_data_key_errors: true },
            adaptation: { desktop: { preload: true }, mobile: { preload: true } },
        } as PlayerConfig;
    }, [adTagUrl, playerKey, shouldDisableBanner]);

    const customUi = useMemo(
        () => ({
            variantsFactory: () => [{ ui: createAdsUI(true), condition: () => true }],
        }),
        []
    );

    const sourceConfig = useMemo(() => {
        if (shouldDisableBanner) return null;
        return {
            dash: config?.avodConfigs?.outStreamAdsPlaybackUrl,
            poster: background,
        };
    }, [background, shouldDisableBanner]);

    useResumeAdPlayback();

    // ==========================================
    // Callbacks (Helpers)
    // ==========================================
    const reportToMixpanel = useCallback(
        (eventName: string, eventData?: Record<string, any>) => {
            const data = {
                'Ad Type': 'Outstream',
                'Ad ID': currentAdInstanceRef.current?.id || '',
                ...(eventData || {}),
            };
            track(eventName, data);
        },
        [track]
    );

    const getAdDetails = useCallback((ad?: AdBreakInstance | null) => {
        const adBreak = currentAdBreakRef.current?.adBreak;
        if (!adBreak?.ads || !ad) return { currentAdNumber: '', adDuration: '', adsCount: 0 };

        const currentAdNumber = adBreak.ads.findIndex((a) => a.id === ad.id) + 1;
        return {
            adsCount: adBreak.ads.length,
            currentAdNumber: currentAdNumber > 0 ? currentAdNumber : '',
            adDuration: ad.duration || '',
        };
    }, []);

    // ==========================================
    // Callbacks (Ad Events)
    // ==========================================
    const onAdRequested = useCallback(() => {
        reportToMixpanel('Ad Requested', { 'Ad Technology': 'CSAI' });
    }, [reportToMixpanel]);

    const onAdReceived = useCallback(() => {
        reportToMixpanel('Ad Received', { 'Ad Technology': 'CSAI' });
    }, [reportToMixpanel]);

    const onAdBreakStart = useCallback(
        (event: AdBreaksEvent) => {
            adBannerHandler.adsStarted = true;
            currentAdBreakRef.current = event;
            setNoAdCtaAdBreakStartTime(0);
            setupNoAdCtaFromConfig(config as unknown as { avodConfigs?: { noAdCta?: unknown } } | null, 'outstream');
            const firstAdId = event?.adBreak?.ads?.[0]?.id || '';
            if (firstAdId) setOutstreamActiveAdIdForNoAdCta(firstAdId);
            // TODO: this is probably not needed. It's updated on SingleAdStarted
            updateAdsUI(1, event.adBreak.ads.length, 0);
        },
        [config]
    );

    const togglePlaybackOnVisibilityChange = function () {
        if (!playerInstanceRef.current) return;
        const player = playerInstanceRef.current;
        if (document.hidden) {
            // Tab is inactive - pause the player (content or ad)
            if (player.isPlaying()) {
                player.pause();
            }
        } else {
            // Tab is active again - resume the player
            // Optionally check if it was playing before to avoid auto-playing
            // a user-paused video.
            safePlayerPlay(player);
        }
    };

    const onSingleAdStarted = useCallback(
        (event: AdBreaksStartedEvent) => {
            currentAdInstanceRef.current = event.ad;
            setOutstreamActiveAdIdForNoAdCta(event?.ad?.id || '');
            const { currentAdNumber, adDuration, adsCount } = getAdDetails(event.ad);

            if (currentAdNumber) {
                onAdRequested();
                onAdReceived();
                reportToMixpanel('Ad Start', {
                    'User ID': userInfo?.uid || userInfo?.accountId || '',
                    'Ad Sequence Number': currentAdNumber,
                    'Ad Duration': adDuration,
                });
                updateAdsUI(currentAdNumber as number, adsCount, (adDuration as number) * 1000);
            }

            // TODO: move to BaseBitmovinPlayer
            document.addEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
        },
        [getAdDetails, userInfo, reportToMixpanel, onAdRequested, onAdReceived, updateAdsUI, name, adTagUrl]
    );

    const onSingleAdCompleted = useCallback(
        (event: AdBreaksStartedEvent) => {
            const { currentAdNumber, adDuration } = getAdDetails(event.ad);
            reportToMixpanel('Ad Completed', {
                'User ID': userInfo?.uid || userInfo?.accountId || '',
                'Ad Sequence Number': currentAdNumber,
                'Ad Duration': adDuration,
                'Ad Technology': 'CSAI',
            });
        },
        [getAdDetails, userInfo, reportToMixpanel]
    );

    const onTimeChanged = useCallback(() => {
        if (!currentAdInstanceRef.current || !playerInstanceRef.current) return;
        const { adDuration } = getAdDetails(currentAdInstanceRef.current);
        if (adDuration) {
            onAdBeginUI(playerInstanceRef.current, { duration: (adDuration as number) * 1000 });
        }
    }, [getAdDetails, onAdBeginUI]);

    const onAdClicked = useCallback((event: any) => {
        // This is the redirection URL from the AD. when clicked it will redirect user to the advertiser's page. We want to capture this URL and send it to Mixpanel for analytics purposes.
        const adRedirectionURL = currentAdInstanceRef.current?.clickThroughUrl;
        if (!adRedirectionURL || typeof adRedirectionURL !== 'string') return;
        reportToMixpanel('Ad Link Clicked', {
            'Ad Technology': 'CSAI',
            'Ad Link URL': adRedirectionURL,
        });
    }, []);

    // ==========================================
    // Callbacks (Playback & Lifecycle)
    // ==========================================
    const stopContentPlayback = useCallback(
        (stoppedDueToError?: boolean) => {
            const player = playerInstanceRef.current;

            if (player) {
                // Unregistering basic events; avoid circular unregisters on those that depend on stopContentPlayback.
                player.off(PlayerEvent.AdStarted, onSingleAdStarted as any);
                player.off(PlayerEvent.AdBreakStarted, onAdBreakStart as any);
                player.off(PlayerEvent.AdFinished, onSingleAdCompleted as any);
                player.off(PlayerEvent.TimeChanged, onTimeChanged);
                player.off(PlayerEvent.AdClicked, onAdClicked as any);
                player.pause();
            }
            // Give the Youbora ad adapter a brief window (500ms) to flush its final metrics
            // (Ad Completion Rate, AdPlayBack Errors, Avg Watch Time) to its backend
            // before completely destroying the tracking plugins.
            setOutstreamPlayerForNoAdCta(null);
            setAdPodCompleted(true);
            setTimeout(() => {
                removeYBAdsAdapter();
                removeYBAdapter();
            }, 500);
        },
        [onSingleAdStarted, onAdBreakStart, onSingleAdCompleted, onTimeChanged]
    );

    const onAllAdCompleted = useCallback(() => {
        hideNoAdCta();
        setNoAdCtaAdBreakStartTime(null);
        currentAdInstanceRef.current = null;
        setOutstreamActiveAdIdForNoAdCta('');
        stopContentPlayback();
        document.removeEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
    }, [stopContentPlayback]);

    // ==========================================
    // Callbacks (User Exit & Visibility)
    // ==========================================
    const userExitDuringAd = useCallback(
        (exitReason: string) => {
            if (adBannerHandler.userExitDuringAd || !adBannerHandler.adsStarted) {
                return;
            }

            adBannerHandler.userExitDuringAd = true;
            const ad = currentAdInstanceRef.current;
            if (!ad) return;

            const { currentAdNumber, adDuration } = getAdDetails(ad);
            let timeWatched = 0;
            let completionRate = 0;

            if (typeof adDuration === 'number' && playerInstanceRef.current) {
                const currentTime = playerInstanceRef.current.getCurrentTime() || 0;
                timeWatched = Math.floor(currentTime);
                completionRate = Math.floor(Math.min(Math.max((currentTime / adDuration) * 100, 0), 100));
            }

            reportToMixpanel('User Exited During Ad', {
                'User ID': userInfo?.uid || userInfo?.accountId || '',
                'Ad Duration': adDuration,
                'Ad Sequence Number': currentAdNumber,
                'Time Watched': timeWatched,
                'Completion Rate': completionRate,
                'Exit Reason': exitReason,
            });
        },
        [getAdDetails, userInfo, reportToMixpanel]
    );

    const onVisibilityChange = useCallback(
        (isVisible: boolean) => {
            setExpandVisible(isVisible);
            if (isVisible && !expandWasVisibleRef.current) {
                setAdPodCompleted(false);
                setPlayerMountKey((k) => k + 1);
                adBannerHandler.adsStarted = false;
                adBannerHandler.userExitDuringAd = false;
            } else if (!isVisible) {
                setOutstreamPlayerForNoAdCta(null);
                userExitDuringAd('User scrolled away from the ad');
            }
            expandWasVisibleRef.current = isVisible;
        },
        [userExitDuringAd]
    );

    // ==========================================
    // Callbacks (Player Init)
    // ==========================================
    const handlePlayerRef = useCallback(
        (player: PlayerAPI) => {
            playerInstanceRef.current = player;
            setOutstreamPlayerForNoAdCta(player);

            let contentId = 'tempId';
            let title = 'Outstream Ad Banner';

            const analyticsMetadata = adConfig?.analyticsMetadata;
            if (analyticsMetadata) {
                contentId = analyticsMetadata.contentId || contentId;
                title = analyticsMetadata.title || title;
            }

            // Use adTagUrl instead of the dummy video for Youbora tracking.
            const outstreamAsset = {
                guid: contentId,
                title: title,
                duration: currentAdInstanceRef.current?.duration || 0,
                programType: 'Outstream',
                genre: '',
                ratings: profileInfo?.newParentalControl || profileInfo?.parentalControl || '',
                year: new Date().getFullYear(),
                adEnabled: true,
                Id: carousel?.mixpanelKey || 'NA',
                isGuestMode: carousel?.guestModeActive === true,
                requestedURL: config?.avodConfigs?.outStreamAdsPlaybackUrl,
                redirectedUrl: config?.avodConfigs?.outStreamAdsPlaybackUrl,
            };

            updateYouboraAnalytics(outstreamAsset, adTagUrl, playerTypes.Outstream, 'NA', true, false);

            setYBPluginOptions(playerInstanceRef.current);
            setYBAdPluginOptions(playerInstanceRef.current);

            player.on(PlayerEvent.AdStarted, onSingleAdStarted as unknown as (...args: unknown[]) => void);
            player.on(PlayerEvent.AdBreakStarted, onAdBreakStart as unknown as (...args: unknown[]) => void);
            player.on(PlayerEvent.AdFinished, onSingleAdCompleted as unknown as (...args: unknown[]) => void);
            player.on(PlayerEvent.TimeChanged, onTimeChanged);
            player.on(PlayerEvent.AdClicked, onAdClicked);
            player.on(PlayerEvent.AdBreakFinished, onAllAdCompleted);
            player.on(PlayerEvent.AdError, (err) => {
                stopContentPlayback(true);
            });
            player.on(PlayerEvent.Error, (err) => {
                stopContentPlayback(true);
            });
        },
        [
            onSingleAdStarted,
            onAdBreakStart,
            onSingleAdCompleted,
            onTimeChanged,
            onAllAdCompleted,
            stopContentPlayback,
            name,
            adTagUrl,
        ]
    );

    useEffect(() => {
        const handlePageHide = () => {
            userExitDuringAd('User closed the tab or navigated away');
        };

        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            setOutstreamPlayerForNoAdCta(null);
            setOutstreamActiveAdIdForNoAdCta('');
            removeYBAdsAdapter();
            removeYBAdapter();
        };
    }, [userExitDuringAd]);

    // ==========================================
    // Render
    // ==========================================
    if (!isFreeTierSub && !isGuest) {
        return <></>;
    }

    return (
        <div
            className="container full-width outstream-banner ad-banner"
            style={{
                position: 'relative',
                overflow: 'hidden',
            }}
            ref={ref}
        >
            <div className={styles['outstream-ad-container']}>
                <div
                    style={{
                        top: 0,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        zIndex: 20,
                        pointerEvents: 'none',
                        position: 'absolute',
                        inset: 0,
                        height: '100%',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            transform: 'none',
                            height: 'calc(100% - var(--step) * 4)',
                            position: 'absolute',
                            inset: 0,
                            overflow: 'hidden',
                            transformOrigin: 'top center',
                            pointerEvents: 'auto',
                        }}
                        className={styles['expand-card']}
                    >
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                            {/* <ExpandArea onVisibilityChange={onVisibilityChange} fixedHeight>
                {expandVisible && !adPodCompleted && !shouldDisableBanner ? ( */}
                            {inView && !adPodCompleted && !shouldDisableBanner ? (
                                <>
                                    {/* <div style={{ height: '100%', alignContent: 'center' }}> */}
                                    <BaseBitmovinPlayer
                                        key={playerMountKey}
                                        playerRef={handlePlayerRef}
                                        config={playerConfig}
                                        source={sourceConfig}
                                        customUi={customUi}
                                        onBeforeSourceLoaded={adBannerHandler.beforeSourceLoaded}
                                        // TODO: move to BaseBitmovinPlayer?
                                        className={styles.baseplayer}
                                    />
                                    {/* </div> */}
                                    {/* {showNoAdCta && (
                            <Button className={styles['no-ad-cta-button']} onClick={onNoAdCtaClick}>
                                {__('no_ads_cta')}
                            </Button>
                        )} */}
                                </>
                            ) : (
                                background && (
                                    <div style={{ paddingBottom: '56.25%', position: 'relative' }}>
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                insetInlineEnd: 0,
                                                background: 'var(--primary-colors-orange)',
                                                textTransform: 'uppercase',
                                                fontSize: 'mix.rem(14px)',
                                                lineHeight: 'mix.rem(22px)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: '#fff',
                                                padding: '0 var(--step)',
                                                borderEndStartRadius: 'var(--radius)',
                                                zIndex: 2,
                                            }}
                                        >
                                            AD
                                        </span>
                                        <img
                                            src={background}
                                            alt=""
                                            role="presentation"
                                            style={{
                                                height: '100%',
                                                width: '100%',
                                                objectFit: 'cover',
                                                position: 'absolute',
                                            }}
                                        />
                                    </div>
                                )
                            )}
                            {/* </ExpandArea> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdBanner;
